import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import * as Popover from '@radix-ui/react-popover';
import React from 'react';

import { PopoverContent } from './Animations';
import { Button } from './Button';
import { cn } from './cn';

export interface SelectableMenuItem {
  key: string;
  text?: string;
  secondaryText?: string;
  canCheck?: boolean;
  checked?: boolean;
  onClick?: () => void;
  [key: string]: any;
}

export interface SelectableItemsCalloutProps {
  buttonText?: React.ReactNode;
  menuItems: SelectableMenuItem[];
  searchBoxPlaceholderText?: string;
  emptyItemsText?: string;
  calloutNoItemsText?: string;
  calloutNoItemsIconName?: string;
  minimal?: boolean;
  openContextMenuIfNoItems?: boolean;
  className?: string;
  styles?: { contextualMenu?: { width?: string | number } };
}

const SelectableItemsCallout = ({
  buttonText,
  menuItems,
  searchBoxPlaceholderText = 'Search items',
  emptyItemsText,
  calloutNoItemsText = 'No items found.',
  minimal,
  openContextMenuIfNoItems,
  className,
}: SelectableItemsCalloutProps) => {
  const [open, setOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState('');

  const filteredItems = React.useMemo(() => {
    if (!menuItems.length) {
      return openContextMenuIfNoItems ? [] : [];
    }
    if (!searchText) return menuItems;
    const lower = searchText.toLowerCase();
    return menuItems.filter(
      (item) => item.text?.toLowerCase().includes(lower) || item.secondaryText?.toLowerCase().includes(lower)
    );
  }, [menuItems, searchText, openContextMenuIfNoItems]);

  const hasItems = menuItems.length > 0;
  const hasResults = filteredItems.length > 0;

  return (
    <Popover.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearchText('');
      }}
    >
      <Popover.Trigger asChild>
        <Button variant={minimal ? 'ghost' : 'outline'} size="sm" className={cn('text-sm', className)}>
          {buttonText || 'Select items'}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-64 rounded-lg border border-border bg-surface shadow-lg"
          sideOffset={4}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
          asChild
        >
          <PopoverContent>
            {hasItems && (
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder={searchBoxPlaceholderText}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full border-none bg-transparent text-sm text-text-primary placeholder:text-gray-400 focus:outline-none"
                  autoFocus
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="shrink-0 text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer p-0"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
            <div className="max-h-60 overflow-auto py-1">
              {!hasItems && emptyItemsText ? (
                <p className="px-3 py-4 text-center text-sm text-gray-400">{emptyItemsText}</p>
              ) : hasResults ? (
                filteredItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => item.onClick?.()}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 cursor-pointer bg-transparent border-none'
                    )}
                  >
                    {item.canCheck && (
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                          item.checked ? 'border-info-500 bg-info-500 text-white' : 'border-gray-300 bg-surface'
                        )}
                      >
                        {item.checked && (
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="truncate">{item.text}</span>
                    {item.secondaryText && (
                      <span className="ml-auto shrink-0 text-xs text-gray-400">{item.secondaryText}</span>
                    )}
                  </button>
                ))
              ) : (
                <p className="px-3 py-4 text-center text-sm text-gray-400">{calloutNoItemsText}</p>
              )}
            </div>
          </PopoverContent>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default SelectableItemsCallout;

export const toMenuItem =
  (selectedItems: string[] | undefined, setNewSelectedItems: (newItems: string[]) => void) =>
  (item: { key: string; text: string; secondaryText?: string }): SelectableMenuItem => {
    const activeIdx = !selectedItems ? -1 : selectedItems.indexOf(item.key);
    return {
      ...item,
      canCheck: true,
      checked: activeIdx !== -1,
      onClick: () => {
        setNewSelectedItems(
          activeIdx === -1
            ? [...(selectedItems || []), item.key]
            : (selectedItems || []).filter((_, i) => i !== activeIdx)
        );
      },
    };
  };

export interface SelectedCountBadgeProps {
  children?: React.ReactNode;
}

export const SelectedCountBadge = ({ children }: SelectedCountBadgeProps) => (
  <span className="inline-flex items-center rounded-full bg-yellow-400 px-2 text-xs font-medium text-gray-900 scale-[0.8]">
    {children}
  </span>
);
